"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type PropsWithChildren } from "react";
import type { UsersLoginSuccess } from "../client";
import {
  serializeUsersSession,
  parseUsersSession,
  isUsersSessionExpired,
  getUsersSessionFromCookieString,
} from "../session";

export interface SessionConfig {
  /**
   * Name of the cookie to store session in
   * @example "px_consumer_session", "px_manager_session"
   */
  cookieName: string;
  
  /**
   * Key to use for localStorage
   * @example "consumer_session", "manager_session"
   */
  storageKey: string;
}

export interface SessionContextValue {
  /**
   * Current session, null if not authenticated
   */
  session: UsersLoginSuccess | null;
  
  /**
   * Whether the session has been hydrated from storage
   */
  isHydrated: boolean;
  
  /**
   * Set a new session (stores in cookie and localStorage)
   */
  setSession: (session: UsersLoginSuccess | null) => void;
  
  /**
   * Clear the current session
   */
  logout: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

function loadSession(config: SessionConfig): UsersLoginSuccess | null {
  if (typeof window === "undefined") {
    return null;
  }

  // Load from localStorage first
  const storedValue = window.localStorage.getItem(config.storageKey);
  let storedSession = parseUsersSession(storedValue);

  // Check if stored session is expired
  if (storedSession && isUsersSessionExpired(storedSession)) {
    window.localStorage.removeItem(config.storageKey);
    storedSession = null;
  }

  // Load from cookie
  const cookieSession = getUsersSessionFromCookieString(document.cookie, config.cookieName);
  
  if (!cookieSession) {
    // Cookie doesn't exist, clear localStorage too
    if (storedSession) {
      window.localStorage.removeItem(config.storageKey);
    }
    return null;
  }

  // Check if cookie session is expired
  if (isUsersSessionExpired(cookieSession)) {
    clearSession(config);
    return null;
  }

  // Sync cookie to localStorage if different
  if (
    !storedSession ||
    storedSession.session.accessToken !== cookieSession.session.accessToken ||
    storedSession.session.refreshToken !== cookieSession.session.refreshToken
  ) {
    window.localStorage.setItem(config.storageKey, serializeUsersSession(cookieSession));
  }

  return cookieSession;
}

function persistSession(session: UsersLoginSuccess, config: SessionConfig): void {
  if (typeof document === "undefined") {
    return;
  }

  const payload = serializeUsersSession(session);
  const maxAge = Math.max(0, session.session.expiresInSeconds);
  
  // Set cookie
  document.cookie = `${config.cookieName}=${payload}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
  
  // Set localStorage
  if (typeof window !== "undefined") {
    window.localStorage.setItem(config.storageKey, payload);
  }
}

function clearSession(config: SessionConfig): void {
  if (typeof document !== "undefined") {
    // Clear cookie
    document.cookie = `${config.cookieName}=; Path=/; Max-Age=0; SameSite=Lax`;
  }
  
  if (typeof window !== "undefined") {
    // Clear localStorage
    window.localStorage.removeItem(config.storageKey);
  }
}

export interface SessionProviderProps extends PropsWithChildren {
  config: SessionConfig;
}

/**
 * SessionProvider manages user authentication state with cookie and localStorage persistence
 * Can be configured per-app with different cookie names
 */
export function SessionProvider({ config, children }: SessionProviderProps): React.ReactElement {
  const [session, setSessionState] = useState<UsersLoginSuccess | null>(() => {
    if (typeof window === "undefined") {
      return null;
    }
    return loadSession(config);
  });
  const [isHydrated, setHydrated] = useState(false);

  useEffect(() => {
    const resolved = loadSession(config);
    setSessionState(resolved);
    setHydrated(true);

    function handleStorage(event: StorageEvent) {
      if (event.key === config.storageKey) {
        setSessionState(loadSession(config));
      }
    }

    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, [config]);

  const setSession = useCallback(
    (value: UsersLoginSuccess | null) => {
      if (value) {
        persistSession(value, config);
      } else {
        clearSession(config);
      }
      setSessionState(value);
    },
    [config],
  );

  const logout = useCallback(() => {
    setSession(null);
  }, [setSession]);

  const value = useMemo<SessionContextValue>(
    () => ({ session, setSession, logout, isHydrated }),
    [session, setSession, logout, isHydrated],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/**
 * Hook to access session context
 * Must be used within a SessionProvider
 */
export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
}

